import { useState, useMemo, useEffect } from 'react'
import { Download, Copy, Check } from 'lucide-react'
import { Button, cn } from '@valiant/ui-shared'
import QRCode from 'react-qr-code'
import { toast } from 'sonner'
import { useNavigation } from '../../../contexts/NavigationContext'
import { useGetAllMintsQuery, useGetCompanyDepositWalletsQuery } from '../../../generated/graphql'
import { CoinSelector } from '../CoinSelector'
import { NetworkSelector } from '../NetworkSelector'

export function DepositFundsContent({ className }: { className?: string }) {
  const [selectedSymbol, setSelectedSymbol] = useState<string>('')
  const [selectedMintId, setSelectedMintId] = useState<string>('')
  const [copiedAddress, setCopiedAddress] = useState(false)

  const { selectedCompanyId, selectedCompany } = useNavigation()

  // Fetch all available mints
  const [{ data: mintsData, fetching: mintsLoading }] = useGetAllMintsQuery({
    variables: { companyId: selectedCompanyId || '' },
    pause: !selectedCompanyId,
  })

  // Fetch deposit wallets
  const [{ data: walletsData, fetching: walletsLoading }] = useGetCompanyDepositWalletsQuery({
    variables: { companyId: selectedCompanyId || '' },
    pause: !selectedCompanyId,
  })

  // Process available mints
  const availableMints = useMemo(() => {
    const mints = mintsData?.getAllMints || []
    return mints.map((mint) => ({
      id: mint.id,
      symbol: mint.symbol,
      name: mint.name,
      chain: mint.chain,
      image: mint.image,
    }))
  }, [mintsData])

  // Unique tokens (deduplicated by symbol)
  const uniqueTokens = useMemo(() => {
    const seen = new Set<string>()
    return availableMints.filter((mint) => {
      if (seen.has(mint.symbol)) return false
      seen.add(mint.symbol)
      return true
    })
  }, [availableMints])

  // Mints available for the selected symbol
  const networksForSymbol = useMemo(() => {
    if (!selectedSymbol) return []
    return availableMints.filter((mint) => mint.symbol === selectedSymbol)
  }, [availableMints, selectedSymbol])

  // Get selected mint details
  const selectedMint = useMemo(() => {
    return availableMints.find((mint) => mint.id === selectedMintId)
  }, [availableMints, selectedMintId])

  // Find corresponding deposit address
  const depositAddress = useMemo(() => {
    if (!selectedMint || !walletsData?.getCompanyDepositWallets) return ''

    const wallet = walletsData.getCompanyDepositWallets.find(
      (wallet) => wallet.chain === selectedMint.chain,
    )

    return wallet?.address || ''
  }, [selectedMint, walletsData])

  // Auto-select first token symbol on load
  useEffect(() => {
    if (uniqueTokens.length > 0 && !selectedSymbol) {
      setSelectedSymbol(uniqueTokens[0].symbol)
    }
  }, [uniqueTokens, selectedSymbol])

  // When selected symbol changes, auto-select network if only one exists
  useEffect(() => {
    if (!selectedSymbol) return
    const networks = availableMints.filter((mint) => mint.symbol === selectedSymbol)
    if (networks.length === 1) {
      setSelectedMintId(networks[0].id)
    } else {
      setSelectedMintId('')
    }
  }, [selectedSymbol, availableMints])

  const handleCopyAddress = async () => {
    if (!depositAddress) return

    try {
      await navigator.clipboard.writeText(depositAddress)
      setCopiedAddress(true)
      toast.success('Address copied to clipboard')
      setTimeout(() => setCopiedAddress(false), 2000)
    } catch (error) {
      toast.error('Failed to copy address')
      console.error('Copy failed:', error)
    }
  }

  const isLoading = mintsLoading || walletsLoading

  return (
    <div className={cn('flex flex-col h-full p-5', className)}>
      <div className="mb-12.5">
        <div className="flex items-center gap-2 ">
          <Download className="w-5 h-5" />
          <h3 className="font-semibold text-2xl">Deposit Funds</h3>
        </div>
        <p className="text-2xl text-card-text mt-5 leading-none font-semibold">
          {selectedCompany?.company}
        </p>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-4">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          </div>
        ) : uniqueTokens.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Download className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-medium mb-1">No currencies available</p>
            <p className="text-sm">This company has no deposit currencies configured.</p>
          </div>
        ) : (
          <>
            <CoinSelector
              value={selectedSymbol}
              onValueChange={setSelectedSymbol}
              coins={uniqueTokens}
            />

            <NetworkSelector
              value={selectedMintId}
              onValueChange={setSelectedMintId}
              networks={networksForSymbol}
            />

            {/* Deposit Address */}
            {selectedMint && (
              <div className="mb-12.5 md:mb-25 space-y-2">
                <div className="flex items-center gap-2 justify-between">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Address</label>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyAddress}
                    className="shrink-0 has-[>svg]:px-1.5 text-xs p-1 md:p-0"
                  >
                    {copiedAddress ? (
                      <>
                        <Check className="size-3 text-green-600" /> <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="size-3 stroke-1 md:stroke-2" /> <span>Copy</span>
                      </>
                    )}
                  </Button>
                </div>
                <div className="flex items-center gap-2 relative flex-wrap">
                  <div className="flex-1 px-3 py-3 border border-gray-300 rounded-md text-xs text-gray-900">
                    <span>{depositAddress || 'No address available'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* QR Code */}
            {depositAddress && (
              <div className="text-center">
                <div className="inline-block p-4 bg-white border-2 border-main rounded-lg">
                  <QRCode
                    value={depositAddress}
                    size={160}
                    fgColor="currentColor"
                    className="text-main"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2 font-medium">Scan the QR Code</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
